import { Injectable, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, of, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  status: string;
  roles: string[];
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: AuthUser;
}

const ACCESS_KEY = 'encuesta.accessToken';
const REFRESH_KEY = 'encuesta.refreshToken';
const USER_KEY = 'encuesta.user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userSignal = signal<AuthUser | null>(this.readUser());
  readonly user = this.userSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.userSignal() && !!this.getAccessToken());
  readonly isAdmin = computed(() => (this.userSignal()?.roles ?? []).includes('ADMIN'));

  private refreshInFlight: Observable<AuthResponse> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${environment.apiUrl}/api/v1/auth/login`, { email, password })
      .pipe(tap((response) => this.persistSession(response)));
  }

  refresh(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.http
        .post<AuthResponse>(`${environment.apiUrl}/api/v1/auth/refresh`, { refreshToken })
        .pipe(
          tap((response) => this.persistSession(response)),
          catchError((err) => {
            this.clearLocalSession();
            return throwError(() => err);
          }),
          finalize(() => {
            this.refreshInFlight = null;
          }),
        );
    }
    return this.refreshInFlight;
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();
    const clear$ = refreshToken
      ? this.http.post(`${environment.apiUrl}/api/v1/auth/logout`, { refreshToken }).pipe(
          catchError(() => of(null)),
        )
      : of(null);

    clear$.subscribe({
      next: () => {
        this.clearLocalSession();
        void this.router.navigateByUrl('/login');
      },
      error: () => {
        this.clearLocalSession();
        void this.router.navigateByUrl('/login');
      },
    });
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  }

  private persistSession(response: AuthResponse): void {
    localStorage.setItem(ACCESS_KEY, response.accessToken);
    localStorage.setItem(REFRESH_KEY, response.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(response.user));
    this.userSignal.set(response.user);
  }

  private clearLocalSession(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    this.userSignal.set(null);
  }

  private readUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }
}
